var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import ReservationRequest from './reservationRequest';
import { ReservationRequestAction, ReservationType, } from './types';
import { forOwn } from '../../utils';
import { ShopConfig, } from '../../shop/config';
import { errorToSlugError, slugError, } from '../../internals/errors/errors';
/**
 *  The reservator is a manager for reservation requests.
 *
 *  Any limits and request queues are held within a reservator.
 *
 *  @internal
 */
export default class Reservator {
    /**
     *  The constructor initializes a new reservator instance and checks if all (relevant) values in the settings object are valid
     *
     *  @internal
     */
    constructor(config) {
        this._queue = [];
        this._requestSlots = 0;
        this._typePendingCount = {};
        if (!config) {
            throw slugError('No valid settings provided for the reservator', ['reservator', 'constructor', 'error', 'config']);
        }
        this._config = new ShopConfig(config);
    }
    /**
     *  The reserve method will queue a reservation request and ensure it will be send within the limitations set in the settings.
     *
     *  These limitations will ensure the server as well as the client are not swamped with requests.
     *
     *  Limitations are placed on:
     *  - Total pending requests ('in the air' + waiting)
     *  - Total pending requests 'in the air'
     *
     *  It is possible to request multiple reservations in bulk by providing the numberOfReservations amount.
     *  @todo Right now reservation requests are handled individually, another efficiency step here would be to implement bulk reservations.
     *    These bulk requests would allow higher volumes to be requested by the the user, while keeping request congestion to a minimum.
     *
     *  @internal
     */
    reserve(type, guid, numberOfReservations = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check the limit on total pending reservations (both 'in the air' and waiting)
            if ((this._queue.length + this._requestSlots) >= this._config.reservator.maxPendingReservations) {
                throw slugError('Max pending reservations reached', ['reservator', 'reserve', 'error', 'maxpending']);
            }
            const reservationRequest = new ReservationRequest(type, guid, numberOfReservations);
            // The actual requests should only be send when the queue is not (no longer) overfull.
            this._queue.push(reservationRequest);
            this._send().then();
            let reservations;
            try {
                // Return only the promise in the reservation request.
                // The actual reservation request has no purpose living outside the reservator.
                reservations = yield reservationRequest._promise;
            }
            catch (error) {
                if (error && error.response && error.response.status === 406) {
                    throw errorToSlugError(error, ['reservator', 'reserve', 'error', 'notacceptable']);
                }
                throw errorToSlugError(error, ['reservator', 'reserve', 'error', 'failed']);
            }
            if (!reservations || !Array.isArray(reservations) || !reservations.length) {
                throw slugError('Reserve failed -> Reservations missing in response', ['reservator', 'reserve', 'error', 'reservation']);
            }
            const filteredReservations = reservations.filter((reservation) => {
                return !!reservation;
            });
            if (filteredReservations.length !== numberOfReservations) {
                throw slugError('Reserve failed -> (Some) reservation(s) missing in response', ['reservator', 'reserve', 'error', 'reservations']);
            }
            return filteredReservations;
        });
    }
    /**
     *  The reserve method will queue a child reservation request and ensure it will be send within the limitations set in the settings.
     *
     *  These limitations will ensure the server as well as the client are not swamped with requests.
     *
     *  Limitations are placed on:
     *  - Total pending requests ('in the air' + waiting)
     *  - Total pending requests 'in the air'
     *
     *  It is possible to request multiple reservations in bulk by providing the numberOfReservations amount.
     *  @todo Right now reservation requests are handled individually, another efficiency step here would be to implement bulk reservations.
     *    These bulk requests would allow higher volumes to be requested by the the user, while keeping request congestion to a minimum.
     *
     *  @internal
     */
    reserveChild(type, guid, childType, childGuid, numberOfReservations = 1) {
        return __awaiter(this, void 0, void 0, function* () {
            // Check the limit on total pending reservations (both 'in the air' and waiting)
            if ((this._queue.length + this._requestSlots) >= this._config.reservator.maxPendingReservations) {
                throw slugError('Max pending reservations reached', ['reservator', 'reserveChild', 'error', 'maxpending']);
            }
            const reservationRequest = new ReservationRequest(type, guid, numberOfReservations).forChild(childType, childGuid);
            // The actual requests should only be send when the queue is not (no longer) overfull.
            this._queue.push(reservationRequest);
            this._send().then();
            let reservations;
            try {
                // Return only the promise in the reservation request.
                // The actual reservation request has no purpose living outside the reservator.
                reservations = yield reservationRequest._promise;
            }
            catch (error) {
                if (error && error.response && error.response.status === 406) {
                    throw errorToSlugError(error, ['reservator', 'reserveChild', 'error', 'notacceptable']);
                }
                throw errorToSlugError(error, ['reservator', 'reserveChild', 'error', 'failed']);
            }
            if (!reservations || !reservations.length) {
                throw slugError('Reserve child failed -> Reservations missing in response', ['reservator', 'reserveChild', 'error', 'reservation']);
            }
            const filteredReservations = reservations.filter((reservation) => {
                return !!reservation;
            });
            if (filteredReservations.length !== numberOfReservations) {
                throw slugError('Reserve child failed -> (Some) reservation(s) missing in response', ['reservator', 'reserveChild', 'error', 'reservations']);
            }
            return filteredReservations;
        });
    }
    /**
     *  The release method will queue a release request and ensure it will be send within the limitations set in the settings.
     *
     *  @internal
     */
    release(type, guid, reservation) {
        return __awaiter(this, void 0, void 0, function* () {
            const reservationRequest = new ReservationRequest(type, guid, 1).release(reservation);
            this._queue.push(reservationRequest);
            this._send().then();
            // Ignores any response from the promise
            yield reservationRequest._promise;
        });
    }
    /**
     *  The send method will take queued reservation requests as long as there are requests in the queue.
     *  This will allow any call over the concurrent limit to this method to silently return and
     *  trust the request it was supposed to handle will still be handled.
     *
     *  There is a configurable, limited amount of calls to this method allowed to be running at the same time.
     *  This protects the client and server from too many consecutive alive requests.
     *
     *  If the API returned a reservation response, this will be marshalled into a reservation object and
     *  used as the promise resolution value.
     *
     *  @internal
     */
    _send() {
        return __awaiter(this, void 0, void 0, function* () {
            // There is a configurable maximum of in flight requests.
            // Exclusively acquire one of the request slots to ensure no more requests than allowed are in the air.
            if (++this._requestSlots > this._config.reservator.maxConcurrentReservations) {
                // All available request slots are used by other senders.
                // Any sender who finishes holds the responsibility to ensure it does not finish until the queue is empty.
                // This will ensure the current call to _send can safely (and silently) abort and
                // trust that the intended work will be performed by the other senders already/still running.
                this._requestSlots--;
                return;
            }
            let job;
            while ((job = this._queue.shift())) {
                try {
                    if (job.action === ReservationRequestAction.Reserve) {
                        const guid = job.childGuid || job.guid;
                        const numberOfReservations = job.numberOfReservations;
                        const typeCounts = this._typePendingCount[job.childType || job.type] = this._typePendingCount[job.childType || job.type] || {};
                        typeCounts[guid] = typeCounts[guid] || 0;
                        typeCounts[guid] += numberOfReservations;
                        const promises = [];
                        for (let i = 0; i < numberOfReservations; i++) {
                            // TODO: When API can handle `numberOfReservations` -> add this here as well.
                            // Note URL is recompiled for every request, due to unique cache busting param
                            const url = this.compileUrl(job.type, job.guid, job.childType, job.childGuid);
                            // Transforming any rejections into standard responses allows waiting for ALL failures,
                            // instead of only the first one.
                            promises.push(this._config.httpClient.get(url)
                                .then((response) => Promise.resolve({
                                payload: response.data,
                            }))
                                .catch((reason) => Promise.resolve({
                                error: reason,
                            })));
                        }
                        const result = yield Promise.all(promises)
                            .then((results) => {
                            const failures = results
                                .filter((result) => result.error);
                            if (failures.length) {
                                // Only rejecting with first error!
                                return Promise.reject(failures[0].error);
                            }
                            return results.map((result) => marshalReservationResponse(result.payload));
                        })
                            .finally(() => {
                            typeCounts[guid] -= numberOfReservations;
                        });
                        // This is not the place an error should be thrown if the API returns an empty response.
                        // The originating function should check this!
                        job.resolve(result);
                    }
                    else {
                        // Note URL is recompiled here, due to unique cache busting param
                        const url = this.compileUrl(job.type, job.guid, job.childType, job.childGuid);
                        yield this._config.httpClient.delete(url);
                        job.resolve();
                    }
                }
                catch (error) {
                    // A rejected promise from the reservation call will be caught here and propagated to the reservation request's promise.
                    // Any other reservation request will still be handled!
                    job.reject(error);
                }
            }
            // There are no longer jobs on the queue. It is ok now to release the request slot
            // and make it available to a future call to reserve/_send
            // TODO: There is a race condition here.
            //  If a job was added to the queue if every sender is between the last call to shift and the release of the request slot.
            //  There is a chance the sender of that job has already aborted due to this sender still holding the lock.
            this._requestSlots--;
            return Promise.resolve();
        });
    }
    /**
     *  Create the URL for any reservation calls
     *
     *  @internal
     */
    compileUrl(type, guid, childType, childGuid) {
        let path = `${this._config.guid}/reserve/${type.toLowerCase()}/${guid}`;
        if (childType) {
            path += `/${childType.toLowerCase()}`;
        }
        if (childGuid) {
            path += `/${childGuid}`;
        }
        return this._config.getNoCacheUrl(path);
    }
    /**
     *  Alternative release method to facilitate releasing reservation on window unload.
     *
     *  Note: This is not a perfect solution, as it is not supported in IE at all.
     *
     *  @internal
     */
    releaseOnUnload(type, guid, reservation) {
        // It seems redundant to create a reservation request here.
        // But it allows for keeping all validation logic in one place
        const reservationRequest = new ReservationRequest(type, guid, 1).release(reservation);
        return window.fetch(this.compileUrl(reservationRequest.type, reservationRequest.guid, reservationRequest.childType, reservationRequest.childGuid), {
            method: 'DELETE',
            mode: 'cors',
            cache: 'no-cache',
            keepalive: true,
        });
    }
    /**
     *  This will return the aggregated pending count for a type and guid.
     *
     *  E.g.: The reserve count minus release count
     *
     *  @internal
     */
    inFlightCount(type, guid) {
        if (type && guid) {
            const typed = this._typePendingCount[type] || {};
            if (guid) {
                return typed[guid] || 0;
            }
            return Object.values(typed).reduce((carry, count = 0) => carry + count, 0);
        }
        return Object.values(this._typePendingCount).reduce((carry, typed) => {
            return carry + Object.values(typed).reduce((subCarry, count = 0) => subCarry + count, 0);
        }, 0);
    }
}
/**
 *  Marshals a reservation response to a reservation object.
 *
 *  This method is created as a helper, because the action it performs is simple,
 *  however the implementation is less straight forward / readable.
 *
 *  The only difference between a reservation response and a reservation object is that the type of child reservations are different.
 *  By marshalling the child reservations into a reservation object as well,
 *  we guarantee the usability within the cart can be equal between cart items instantiated through an explicit user action and
 *  because the API decided the cart item should have a child.
 *
 *  E.g.: If this was not done, there would be a difference between a product (e.g.: a required product) added to a ticket
 *  through a child reservation and a product (e.g.: an optional product) added through a separate call.
 */
function marshalReservationResponse(reservationResponse) {
    if (!reservationResponse || !reservationResponse.reservation) {
        return;
    }
    // The top level reservation is easy and should always be set.
    const reservation = {
        received_at: new Date(Date.now()),
        reservation: reservationResponse.reservation,
    };
    // Child reservations are optional. When they are not present, return early
    if (!reservationResponse.childReservations) {
        return reservation;
    }
    // Loop over all possible (child) reservation types
    forOwn(ReservationType, (childReservationType) => {
        if (!Object.prototype.hasOwnProperty.call(reservationResponse.childReservations, childReservationType) || !reservationResponse.childReservations[childReservationType]) {
            // If the child reservation type does not exist in the reservation response,
            // move on to the next one (iff any)
            return;
        }
        reservation.childReservations = reservation.childReservations || {};
        // Loop over every child reservation model. Child reservations are grouped by their type and associated (reservable) model.
        forOwn(reservationResponse.childReservations[childReservationType], (childReservationIds, childId) => {
            reservation.childReservations[childReservationType] = reservation.childReservations[childReservationType] || {};
            reservation.childReservations[childReservationType][childId] = reservation.childReservations[childReservationType][childId] || [];
            // Loop over every child reservation id for the current type and reservable id and add them to the reservation object.
            reservation.childReservations[childReservationType][childId].push(...(childReservationIds || []).map((childReservationId) => {
                return {
                    received_at: new Date(Date.now()),
                    reservation: childReservationId,
                };
            }));
        });
    });
    return reservation;
}
//# sourceMappingURL=reservator.js.map